import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Volume2 } from 'lucide-react';
import { getMyAlertStyle, saveAlertVolume } from '../../../api/payment/alert-style.api';
import { getUserDonateSettingsByUserID } from '../../../api';
import VolumeSlider from '../../../components/volume-slider/volume-slider';
import { AlertVolumes, DEFAULT_ALERT_VOLUMES, applyVolume, readAlertVolumes } from '../../../utils/overlay-volume';
import { synthesizeSpeech } from '../../../utils/public-tts';

// Câu mẫu cho nút "Nghe thử" giọng đọc. Tải một lần rồi giữ lại, bấm nghe nhiều lần không gọi lại máy đọc.
export const VOICE_SAMPLE_TEXT = 'An đã ủng hộ 20.000 Ca. Chúc kênh ngày càng phát triển.';
const DEFAULT_SOUND = 'sound_1.mp3';

type PreviewKind = 'alert' | 'voice';

/**
 * "Âm lượng trên OBS" trong tab Thông báo: hai thanh trượt cho tiếng thông báo và cho giọng đọc kèm ghi âm lời nhắn.
 * Lưu riêng ở payment-service cùng dòng giao diện thông báo; overlay OBS đọc lại trước mỗi donate nên không cần tải lại.
 */
const AlertVolumeSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [volumes, setVolumes] = useState<AlertVolumes>(DEFAULT_ALERT_VOLUMES);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sound, setSound] = useState(DEFAULT_SOUND);
  const [playing, setPlaying] = useState<PreviewKind | null>(null);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const previewRef = useRef<HTMLAudioElement | null>(null);
  // Tăng mỗi lần bắt đầu hoặc dừng nghe thử, và khi rời trang: giọng mẫu tải xong muộn thì không phát nữa.
  const previewSeqRef = useRef(0);
  const voiceSampleRef = useRef<string | null>(null);
  const volumesRef = useRef(volumes);
  volumesRef.current = volumes;

  const load = useCallback(() => {
    setLoadError(false);
    getMyAlertStyle()
      .then((s) => {
        setVolumes(readAlertVolumes(s));
        setLoaded(true);
      })
      .catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    if (!userId) return;
    load();
    // Nghe thử bằng đúng âm thanh mặc định streamer đã chọn trong tab Chung.
    getUserDonateSettingsByUserID(userId)
      .then((res) => {
        if (res?.sound) setSound(res.sound);
      })
      .catch(() => undefined);
  }, [userId, load]);

  const stopPreview = useCallback(() => {
    previewSeqRef.current += 1;
    const a = previewRef.current;
    previewRef.current = null;
    if (a) {
      a.onended = null;
      a.pause();
    }
    setPlaying(null);
  }, []);

  const mountedRef = useRef(true);
  // Rời trang thì dừng âm thanh đang nghe thử và bỏ giọng mẫu đang tải.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopPreview();
    };
  }, [stopPreview]);

  // Kéo thanh trượt lúc đang nghe thử thì nghe thấy ngay mức mới.
  useEffect(() => {
    if (playing === 'alert') applyVolume(previewRef.current, volumes.alertVolume);
    if (playing === 'voice') applyVolume(previewRef.current, volumes.voiceVolume);
  }, [playing, volumes]);

  const startPreview = (kind: PreviewKind, src: string) => {
    stopPreview();
    const a = new Audio(src);
    applyVolume(a, kind === 'alert' ? volumesRef.current.alertVolume : volumesRef.current.voiceVolume);
    a.onended = () => {
      if (previewRef.current === a) stopPreview();
    };
    previewRef.current = a;
    setPlaying(kind);
    a.play().catch(() => {
      if (previewRef.current === a) stopPreview();
      toast.error('Trình duyệt chưa cho phát âm thanh, bạn bấm lại nhé.');
    });
  };

  const toggleAlert = () => {
    if (playing === 'alert') {
      stopPreview();
      return;
    }
    startPreview('alert', `/sound/${sound}`);
  };

  const toggleVoice = async () => {
    if (playing === 'voice') {
      stopPreview();
      return;
    }
    if (loadingVoice) return;
    if (!voiceSampleRef.current) {
      stopPreview();
      const seq = previewSeqRef.current;
      setLoadingVoice(true);
      const tts = await synthesizeSpeech(VOICE_SAMPLE_TEXT);
      // Rời trang, bấm dừng hoặc bấm nghe thử âm thanh khác trong lúc chờ: giữ giọng mẫu cho lần sau, không phát.
      if (seq !== previewSeqRef.current) {
        if (tts) voiceSampleRef.current = tts.src;
        if (mountedRef.current) setLoadingVoice(false);
        return;
      }
      setLoadingVoice(false);
      if (!tts) {
        toast.error('Chưa tải được giọng đọc mẫu, bạn thử lại sau nhé.');
        return;
      }
      voiceSampleRef.current = tts.src;
    }
    startPreview('voice', voiceSampleRef.current);
  };

  const handleSave = () => {
    if (saving || !loaded) return; // chặn bấm hai lần, chưa tải xong thì không ghi đè bằng số mặc định
    setSaving(true);
    saveAlertVolume(volumes)
      .then((s) => {
        setVolumes(readAlertVolumes(s));
        toast.success('Đã lưu âm lượng');
      })
      .catch((err) => toast.error(err?.response?.data?.message || 'Lưu âm lượng không thành công'))
      .finally(() => setSaving(false));
  };

  const previewBtn = (kind: PreviewKind, onClick: () => void, busy = false) => (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-label={kind === 'alert' ? 'Nghe thử âm thanh thông báo' : 'Nghe thử giọng đọc'}
      className="shrink-0 rounded-lg border border-[#C8CCD8] dark:border-slate-600 px-3 py-1 text-sm ui-text hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-60"
    >
      {busy ? 'Đang tải…' : playing === kind ? 'Dừng' : 'Nghe thử'}
    </button>
  );

  return (
    <div className="ui-card mt-[10px]">
      <p className="p-3 flex items-center gap-2 w-full ui-heading font-semibold text-[22px] leading-[27px]">
        <Volume2 size={20} className="text-blue-600" />
        Âm lượng trên OBS
      </p>

      <div className="w-full flex flex-col gap-4 ui-surface-2 p-3">
        <p className="text-sm ui-muted">
          Bạn có thể chỉnh nhỏ lại khi vừa chơi vừa phát trực tiếp. Sau khi bạn lưu, thông báo kế tiếp trên
          OBS sẽ dùng âm lượng mới mà không cần tải lại.
        </p>

        {loadError && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
            <span>Không tải được âm lượng đã lưu, bạn thử lại nhé.</span>
            <button type="button" className="font-medium underline" onClick={load}>
              Thử lại
            </button>
          </div>
        )}

        <VolumeSlider
          id="alert-volume"
          label="Âm thanh thông báo"
          value={volumes.alertVolume}
          onChange={(alertVolume) => setVolumes((v) => ({ ...v, alertVolume }))}
          hint="Mức này dùng cho tiếng chuông lúc thông báo donate hiện lên, kể cả âm thanh riêng bạn đặt cho từng mốc."
          action={previewBtn('alert', toggleAlert)}
        />

        <VolumeSlider
          id="voice-volume"
          label="Giọng đọc và lời nhắn giọng nói"
          value={volumes.voiceVolume}
          onChange={(voiceVolume) => setVolumes((v) => ({ ...v, voiceVolume }))}
          hint="Mức này dùng cho giọng đọc lời nhắn và đoạn ghi âm người xem gửi kèm donate."
          action={previewBtn('voice', toggleVoice, loadingVoice)}
        />

        <div className="flex justify-end">
          <button
            type="button"
            disabled={saving || !loaded}
            onClick={handleSave}
            className="h-11 rounded-lg bg-[#39539E] px-6 text-white disabled:opacity-60"
          >
            {saving ? 'Đang lưu…' : 'Lưu âm lượng'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertVolumeSetting;
